import Vue from 'vue'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { ModalLayout } from '@appbase/templates'
import { TextBoxField, FormGroup } from '@appbase/components/forms'
import TreeView from '@appbase/components/TreeView/TreeView.vue'

import api from '@webparts/api';

@Component({
    components: {
        ModalLayout,
        TextBoxField,
        FormGroup,
        TreeView
    }
})
export default class ExternalShortcutSettingModal extends Vue {
    //
    // ────────────────────────────────────────────────── I ──────────
    //   :::::: P R O P S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────
    //
    @Prop({ type: String }) webpartId!: string;

    categoryList: kyobobook.ShortcutCategory[] = [];
    curCategory: kyobobook.ShortcutCategory = this.setCategoryInitVal();
    shortcutList: kyobobook.Shortcut[] = [];
    curShortcut: kyobobook.Shortcut = this.setShortcutInitVal();

    treeModel: treeView.TreeViewItem<kyobobook.Shortcut>[] = [];

    shortcutSetting: boolean = false;
    categorySetting: boolean = false;

    shortcutImageDeleted: string = '';

    //
    // ──────────────────────────────────────────────────────── I ──────────
    //   :::::: C O M P U T E D : :  :   :    :     :        :          :
    // ──────────────────────────────────────────────────────────────────
    //
    get canSetOrder() {
        if(this.categorySetting && this.categoryList.length > 1) return true;
        if(this.shortcutSetting && this.shortcutList.length > 1) return true;
        return false;
    }

    get canMoveUp() {
        if (this.categorySetting) {
            let currentIdx = this.categoryList.findIndex(c => c.categoryId == this.curCategory.categoryId);

            if(currentIdx != 0) return true;
        }

        if (this.shortcutSetting) {
            let parent = this.curShortcut.categoryId;
            let sameLevelShortcut = this.shortcutList.filter(s => s.categoryId == parent);
            let currentIdx = sameLevelShortcut.findIndex(c => c.shortcutId == this.curShortcut.shortcutId);

            if(currentIdx != 0) return true;
        }

        return false;
    }

    get canMoveDown() {
        if (this.categorySetting) {
            let currentIdx = this.categoryList.findIndex(c => c.categoryId == this.curCategory.categoryId);

            if(currentIdx != this.categoryList.length - 1) return true;
        }

        if (this.shortcutSetting) {
            let parent = this.curShortcut.categoryId;
            let sameLevelShortcut = this.shortcutList.filter(s => s.categoryId == parent);
            let currentIdx = sameLevelShortcut.findIndex(c => c.shortcutId == this.curShortcut.shortcutId);

            if(currentIdx != sameLevelShortcut.length - 1) return true;
        }

        return false;
    }

    //
    // ────────────────────────────────────────────────────── I ──────────
    //   :::::: M E T H O D S : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────
    //
    handleClose() {
        this.closeCurrentModal()
    }

    load() {
        this.treeModel = [];

        let mountPromise: Promise<any>[] = [
            api.kyobobook.getShortcutListAdmin(this.webpartId).then(res => {
                if(res) {
                    res.forEach((r) => {
                        if(r.linkUrl.includes('page/hmac')) {
                            r.linkUrl = decodeURIComponent(r.linkUrl);
                        }
                    });
                    
                    this.shortcutList = res;
                }
            })
        ];

        mountPromise.push(
            api.kyobobook.getShortcutCategoryList(this.webpartId).then(res => {
                if(res) {
                    this.categoryList = res;
                }
            })
        );

        Promise.all(mountPromise).then(() => {
            if (this.categoryList && this.categoryList.length > 0) {
                this.categoryList.forEach(c => {
                    // 먼저 category를 treeModel에 추가
                    this.treeModel.push({
                        id: `C${c.categoryId!}`,
                        parentId: null,
                        displayName: c.title,
                        childs: [],
                        context: {
                            categoryId: c.categoryId,
                            webpartId: c.webpartId,
                            title: c.title,
                            linkUrl: '',
                            order: c.order,
                            isUse: true,
                            isMain: true,
                            isPopup: false
                        }
                    });

                    // 해당 카테고리의 shortcut들을 treeModel에 직접 추가
                    const shortcuts = this.shortcutList.filter(s => s.categoryId == c.categoryId);
                    shortcuts.forEach(s => {
                        this.treeModel.push({
                            id: s.shortcutId!,
                            parentId: `C${c.categoryId!}`,
                            displayName: s.title,
                            childs: [],
                            context: s
                        });
                    });
                });

                // 카테고리가 없는 shortcut treeModel에 추가
                const shortcutsNoCategory = this.shortcutList.filter(s => !s.categoryId || s.categoryId <= 0);
                shortcutsNoCategory.forEach(s => {
                    this.treeModel.push({
                        id: s.shortcutId!,
                        parentId: null,
                        displayName: s.title,
                        childs: [],
                        context: s
                    });
                });
            } else {
                this.treeModel = this.shortcutList.map(s => <treeView.TreeViewItem<kyobobook.Shortcut>>{
                    id: s.shortcutId,
                    parentId: null,
                    displayName: s.title,
                    childs: [],
                    context: s   
                });
            }
        });
    }

    // 카테고리 초기값
    setCategoryInitVal() {
        return {
            webpartId: this.webpartId,
            title: '',
            titleEn: '',
            order: 1
        }
    }

    // 카테고리 추가
    addCategory() {
        this.categorySetting = true;
        this.shortcutSetting = false;
        this.curCategory = this.setCategoryInitVal();
    }

    // 카테고리 삭제
    async deleteCategory() {
        if (!this.curCategory.categoryId) return;
        if(await (window as any).confirmAsync(this.$t('삭제_하시겠습니까').toString())) {
            api.kyobobook.deleteCategory(this.webpartId, this.curCategory.categoryId).then(res => {
                if(res > 0) {
                    (window as any).alertAsync(this.$t('삭제_되었습니다').toString());
                    this.load();
                    this.categorySetting = false;
                } else {
                    (window as any).alertAsync(this.$t('삭제_실패').toString());
                }
            })
        }
    }

    // 카테고리 저장
    async saveCategory() {
        if(await this.checkFieldValidate()) {
            if(!this.curCategory.categoryId) {
                api.kyobobook.createCategory({
                    ...this.curCategory,
                    webpartId: this.webpartId,
                }).then(async res => {
                    if(res > 0) {
                        await (window as any).alertAsync(this.$t('저장되었습니다').toString());
                        this.load();
                        this.categorySetting = false;
                    } else {
                        (window as any).alertAsync(this.$t('저장_실패').toString());
                    }
                })
            } else {
                api.kyobobook.updateCategory({
                    ...this.curCategory,
                    webpartId: this.webpartId,
                }).then(async res => {
                    if(res > 0) {
                        await (window as any).alertAsync(this.$t('수정되었습니다').toString());
                        this.load();
                        this.categorySetting = false;
                    } else {
                        (window as any).alertAsync(this.$t('수정_실패').toString());
                    }
                })
            }
        }
    }

    // 바로가기 초기값
    setShortcutInitVal() {
        return {
            webpartId: this.webpartId,
            title: '',
            linkUrl: '',
            order: 1,
            isUse: true,
            isMain: true,
            isPopup: false
        }
    }

    // 링크 추가
    addLink() {
        this.shortcutSetting = true;
        this.categorySetting = false;
        this.curShortcut = this.setShortcutInitVal();

        if (this.curCategory.categoryId) {
            this.curShortcut.categoryId = this.curCategory.categoryId;
        }
    }

    // 링크 삭제
    async deleteLink() {
        if (!this.curShortcut.shortcutId) return;

        if(await (window as any).confirmAsync(this.$t('삭제_하시겠습니까').toString())) {
            api.kyobobook.deleteShortcut(this.webpartId, this.curShortcut.shortcutId).then(res => {
                if(res > 0) {
                    (window as any).alertAsync(this.$t('삭제_되었습니다').toString());
                    this.load();
                    this.shortcutSetting = false;
                } else {
                    (window as any).alertAsync(this.$t('삭제_실패').toString());
                }
            })
        }
    }

    // 카테고리 및 링크 정렬 변경
    onOrderUpClick() {
        if(!this.canSetOrder) return;

        if(this.categorySetting) {
            let currentIdx = this.categoryList.findIndex(c => c.categoryId == this.curCategory.categoryId);

            if(currentIdx == 0) return;

            let targetCategory = this.categoryList[currentIdx - 1];

            api.kyobobook.swapCategoryOrder({
                webpartId: this.webpartId,
                categoryId1: this.curCategory.categoryId!,
                categoryId2: targetCategory.categoryId!
            }).then(res => {
                this.load();
            });
        }

        if(this.shortcutSetting) {
            let parent = this.curShortcut.categoryId;
            let sameLevelShortcut = this.shortcutList.filter(s => s.categoryId == parent);
            let currentIdx = sameLevelShortcut.findIndex(c => c.shortcutId == this.curShortcut.shortcutId);

            if(currentIdx == 0) return;

            let targetShortcut = sameLevelShortcut[currentIdx - 1];

            api.kyobobook.swapOrder({
                webpartId: this.webpartId,
                shortcutId1: this.curShortcut.shortcutId!,
                shortcutId2: targetShortcut.shortcutId!
            }).then(res => {
                this.load();
            });
        }
    }

    onOrderDownClick() {
        if(!this.canSetOrder) return;

        if(this.categorySetting) {
            let currentIdx = this.categoryList.findIndex(c => c.categoryId == this.curCategory.categoryId);

            if(currentIdx == this.categoryList.length - 1) return;

            let targetCategory = this.categoryList[currentIdx + 1];

            api.kyobobook.swapCategoryOrder({
                webpartId: this.webpartId,
                categoryId1: this.curCategory.categoryId!,
                categoryId2: targetCategory.categoryId!
            }).then(res => {
                this.load();
            });
        }

        if(this.shortcutSetting) {
            let parent = this.curShortcut.categoryId;
            let sameLevelShortcut = this.shortcutList.filter(s => s.categoryId == parent);
            let currentIdx = sameLevelShortcut.findIndex(c => c.shortcutId == this.curShortcut.shortcutId);

            if(currentIdx == sameLevelShortcut.length - 1) return;

            let targetShortcut = sameLevelShortcut[currentIdx + 1];

            api.kyobobook.swapOrder({
                webpartId: this.webpartId,
                shortcutId1: this.curShortcut.shortcutId!,
                shortcutId2: targetShortcut.shortcutId!
            }).then(res => {
                this.load();
            });
        }
    }

    // 링크 저장
    async saveLink() {
        if(this.shortcutImageDeleted != ''){
            this.onImageFileDelete();
        }

        if(await this.checkFieldValidate()) {
            if(this.curShortcut.linkUrl.includes('page/hmac')) {
                var url = new URL(this.curShortcut.linkUrl);

                const params = new URLSearchParams(url.search);
                const redirectUrl = params.get('redirectUrl') ?? '';
    
                const encodedRedirectUrl = encodeURIComponent(redirectUrl);
                params.set('redirectUrl', encodedRedirectUrl);
        
                url.search = params.toString();

                this.curShortcut.linkUrl = url.toString();
            }
            if(!this.curShortcut.shortcutId) {
                api.kyobobook.createShortcut({
                    ...this.curShortcut,
                    webpartId: this.webpartId,
                }).then(async res => {
                    if(res > 0) {
                        await (window as any).alertAsync(this.$t('저장되었습니다').toString());
                        this.load();
                        this.shortcutSetting = false;
                    } else {
                        (window as any).alertAsync(this.$t('저장_실패').toString());
                    }
                })
            } else {
                api.kyobobook.updateShortcut({
                    ...this.curShortcut,
                    webpartId: this.webpartId,
                }).then(async res => {
                    if(res > 0) {
                        await (window as any).alertAsync(this.$t('수정되었습니다').toString());
                        this.load();
                        this.shortcutSetting = false;
                    } else {
                        (window as any).alertAsync(this.$t('수정_실패').toString());
                    }
                })
            }
        }
    }

    async checkFieldValidate() {
        if(this.categorySetting) {
            if(this.curCategory.title.trim().length == 0) {
                await (window as any).alertAsync(this.$t('제목을_입력해주세요').toString());
                return false;
            }
            return true;
        }

        if(this.shortcutSetting) {
            if(this.curShortcut.title.trim().length == 0) {
                await (window as any).alertAsync(this.$t('제목을_입력해주세요').toString());
                return false;
            } else if(this.curShortcut.linkUrl.trim().length == 0) {
                await (window as any).alertAsync(this.$t('링크를_입력해주세요').toString());
                return false;
            }
            return true;
        }
    }

    onShortcutSelected(item: treeView.TreeViewItem<kyobobook.Shortcut>) {
        if (!item.context.shortcutId) {
            this.curCategory = item.context;
            this.categorySetting = true;
            this.shortcutSetting = false;
        } else {
            this.curShortcut = item.context;
            this.shortcutSetting = true;
            this.categorySetting = false;
        }
    }

    onSelectImageClick() {
        var inpElem = this.$refs.inpFile as HTMLInputElement;
        inpElem.click();
    }

    onImageFileChanged(e: Event) {
        var inpElem = e.target as HTMLInputElement;
                
        if(inpElem.files && inpElem.files.length > 0) {
            var targetFile = inpElem.files[0] as any;    
            var request =  new XMLHttpRequest();        
            var formData = new FormData();
            request.addEventListener("load", this.onImageUploaded)                      
            request.open('POST', '/cm/Webpart/WebpartShortcut/api/KBB/uploadImage');
                        
            formData.append('imageFile',  targetFile);
            
            request.send(formData);
        }   
    }

    onImageUploaded(e: ProgressEvent) {
        var request = e.currentTarget as XMLHttpRequest;
        var response = JSON.parse(request.response);        
        this.curShortcut.image = response.result;
    } 

    onDeleteImageClick(){
        if(this.curShortcut.image) {
            this.shortcutImageDeleted = this.curShortcut.image;
            this.curShortcut.image = '';
        }
    }

    onImageFileDelete() {
        var request =  new XMLHttpRequest();        
        request.open('DELETE',`/cm/Webpart/WebpartShortcut/api/KBB/deleteImage?fileName=${this.shortcutImageDeleted}`)
        request.send();
    } 

    getShortcutImageSource(fileName: string) {
        return `/cm/Webpart/WebpartShortcut/api/KBB/getShortcutImage?fileName=${encodeURIComponent(fileName)}`;
    }

    //
    // ────────────────────────────────────────────────────────── I ──────────
    //   :::::: L I F E S T Y L E : :  :   :    :     :        :          :
    // ────────────────────────────────────────────────────────────────────
    //
    mounted() {
        this.load();
    }
}